import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, ShieldCheck, CheckCircle2, XCircle, Trash2, Users, Info, RefreshCw } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';

export default function Admin() {
  const [isAdmin, setIsAdmin] = useState(api.isAdmin());
  if (!isAdmin) return <AdminLogin onSuccess={() => setIsAdmin(true)} />;
  return <AdminPanel onLogout={() => setIsAdmin(false)} />;
}

function AdminLogin({ onSuccess }) {
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await api.adminLogin(u, p);
    setLoading(false);
    if (res.ok) onSuccess();
    else setMsg(res.message);
  };
  return (
    <div className="alpha-bg min-h-screen">
      <Header />
      <section className="max-w-md mx-auto px-4 py-16">
        <div className="text-center">
          <div className="text-[11px] tracking-[0.35em] text-cyan-300/80">RESTRICTED AREA</div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-bold text-white">Admin <span className="text-cyan-gradient">Access</span></h1>
        </div>
        <form onSubmit={submit} className="mt-8 glass-strong rounded-2xl p-6 grid gap-4">
          <label className="text-xs tracking-[0.25em] text-cyan-300/80">
            USERNAME
            <input value={u} onChange={(e) => setU(e.target.value)} className="field-dark mt-1 w-full rounded-lg px-4 py-3" />
          </label>
          <label className="text-xs tracking-[0.25em] text-cyan-300/80">
            PASSWORD
            <input type="password" value={p} onChange={(e) => setP(e.target.value)} className="field-dark mt-1 w-full rounded-lg px-4 py-3" />
          </label>
          {msg && <div className="rounded-lg border px-3 py-2 text-sm border-rose-400/40 bg-rose-500/10 text-rose-200 flex gap-2 items-start"><Info className="h-4 w-4 mt-0.5" /> {msg}</div>}
          <button disabled={loading} className="btn-cyan rounded-lg px-4 py-3 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-2 disabled:opacity-70">
            <LogIn className="h-4 w-4" /> {loading ? 'Signing in...' : 'Login'}
          </button>
        </form>
      </section>
      <Footer />
    </div>
  );
}

function AdminPanel({ onLogout }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const refresh = async (f = filter) => {
    setLoading(true);
    setErr(null);
    const res = await api.listUsers(f);
    setLoading(false);
    if (!res.ok) {
      setErr(res.message);
      if (String(res.message).toLowerCase().includes('admin')) {
        api.adminLogout();
        onLogout();
      }
      return;
    }
    setUsers(res.users || []);
  };

  useEffect(() => { refresh('all'); /* eslint-disable-next-line */ }, []);
  useEffect(() => { refresh(filter); /* eslint-disable-next-line */ }, [filter]);

  const counts = {
    all: users.length,
    pending: users.filter(u => u.status === 'pending').length,
    approved: users.filter(u => u.status === 'approved').length,
    rejected: users.filter(u => u.status === 'rejected').length,
  };

  const handleLogout = async () => {
    await api.adminLogout();
    onLogout();
    navigate('/');
  };

  return (
    <div className="alpha-bg min-h-screen">
      <Header />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] tracking-[0.35em] text-cyan-300/80">ADMIN CONTROL</div>
            <h1 className="font-display mt-1 text-3xl sm:text-4xl font-bold text-white">Verification <span className="text-cyan-gradient">Queue</span></h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => refresh(filter)} className="btn-ghost rounded-lg px-4 py-2 text-xs tracking-widest uppercase inline-flex items-center gap-2">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <Link to="/" className="btn-ghost rounded-lg px-4 py-2 text-xs tracking-widest uppercase">Home</Link>
            <button onClick={handleLogout} className="btn-ghost rounded-lg px-4 py-2 text-xs tracking-widest uppercase">Logout</button>
          </div>
        </div>

        {err && <div className="mt-4 rounded-lg border px-3 py-2 text-sm border-rose-400/40 bg-rose-500/10 text-rose-200 flex gap-2 items-start"><Info className="h-4 w-4 mt-0.5" /> {err}</div>}

        <div className="mt-6 grid gap-3 sm:grid-cols-4">
          {[
            { k: 'all', label: 'Total', color: 'text-white' },
            { k: 'pending', label: 'Pending', color: 'text-amber-300' },
            { k: 'approved', label: 'Approved', color: 'text-emerald-300' },
            { k: 'rejected', label: 'Rejected', color: 'text-rose-300' },
          ].map((s) => (
            <button key={s.k} onClick={() => setFilter(s.k)} className={`glass rounded-2xl p-5 text-left feature-card border ${filter === s.k ? 'border-cyan-400/50' : 'border-transparent'}`}>
              <div className="text-[11px] tracking-[0.3em] text-slate-400">{s.label.toUpperCase()}</div>
              <div className={`mt-1 font-display text-3xl font-bold ${s.color}`}>{counts[s.k]}</div>
            </button>
          ))}
        </div>

        <div className="mt-8 glass rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-cyan-400/10 flex items-center gap-2">
            <Users className="h-4 w-4 text-cyan-300" />
            <div className="text-sm text-white">Traders ({users.length})</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-[11px] tracking-[0.25em] text-slate-400">
                <tr>
                  <th className="text-left px-5 py-3">TRADER ID</th>
                  <th className="text-left px-5 py-3">SUBMITTED</th>
                  <th className="text-left px-5 py-3">STATUS</th>
                  <th className="text-right px-5 py-3">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-10 text-slate-500">{loading ? 'Loading...' : 'No records'}</td></tr>
                )}
                {users.map((u) => (
                  <tr key={u.trader_id} className="border-t border-cyan-400/10 hover:bg-cyan-500/5">
                    <td className="px-5 py-3 font-mono text-white">{u.trader_id}</td>
                    <td className="px-5 py-3 text-slate-300">{new Date(u.created_at).toLocaleString()}</td>
                    <td className="px-5 py-3"><StatusPill status={u.status} /></td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2">
                        <button onClick={async () => { await api.approveUser(u.trader_id); refresh(filter); }} className="btn-ghost rounded-md px-3 py-1.5 text-xs tracking-widest uppercase inline-flex items-center gap-1">
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" /> Approve
                        </button>
                        <button onClick={async () => { await api.rejectUser(u.trader_id); refresh(filter); }} className="btn-ghost rounded-md px-3 py-1.5 text-xs tracking-widest uppercase inline-flex items-center gap-1">
                          <XCircle className="h-3.5 w-3.5 text-rose-300" /> Reject
                        </button>
                        <button onClick={async () => { await api.deleteUser(u.trader_id); refresh(filter); }} className="btn-ghost rounded-md px-3 py-1.5 text-xs tracking-widest uppercase inline-flex items-center gap-1">
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 text-xs text-slate-500 flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5 text-cyan-300" /> Actions are stored in MongoDB via FastAPI.
        </div>
      </section>
      <Footer />
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: 'text-amber-300 border-amber-400/40 bg-amber-500/10',
    approved: 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10',
    rejected: 'text-rose-300 border-rose-400/40 bg-rose-500/10',
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs ${map[status] || ''}`}>{String(status).toUpperCase()}</span>;
}
